import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Coins, Search, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  CheckCircle2, Timer, ShieldCheck, Flame,
} from "lucide-react";

const fmt = (n, ccy = "SGD") => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n || 0);
const pct = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`);

const SORTS = [
  { key: "profit_desc", label: "Highest profit", cmp: (a, b) => (b.profit || 0) - (a.profit || 0) },
  { key: "profit_asc", label: "Lowest profit", cmp: (a, b) => (a.profit || 0) - (b.profit || 0) },
  { key: "margin_desc", label: "Highest margin %", cmp: (a, b) => (b.margin_pct ?? -Infinity) - (a.margin_pct ?? -Infinity) },
  { key: "revenue_desc", label: "Highest revenue", cmp: (a, b) => (b.revenue || 0) - (a.revenue || 0) },
  { key: "expenses_desc", label: "Highest spend", cmp: (a, b) => (b.expenses || 0) - (a.expenses || 0) },
];

const STATUS_TABS = [
  { key: "all", label: "All deals" },
  { key: "open", label: "Open" },
  { key: "ready", label: "Ready to close" },
  { key: "closed", label: "Closed" },
];

export default function CostSummary() {
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("profit_desc");
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["cost-summary-portfolio", status],
    queryFn: () => api.get("/analytics/cost-summary-portfolio", { params: { status } }).then((r) => r.data),
    keepPreviousData: true,
  });

  const totals = data?.totals || {};
  const deals = data?.deals || [];
  const currency = deals[0]?.currency || "SGD";

  const filtered = useMemo(() => {
    const cmp = SORTS.find((s) => s.key === sort)?.cmp || SORTS[0].cmp;
    const qLow = q.trim().toLowerCase();
    return deals
      .filter((d) => !qLow || d.name.toLowerCase().includes(qLow) || (d.stage || "").toLowerCase().includes(qLow))
      .slice()
      .sort(cmp);
  }, [deals, sort, q]);

  const readyCount = deals.filter((d) => d.ready_to_close).length;
  const bestDeal = filtered[0];
  const marginTone = totals.margin_pct == null ? "neutral" : totals.margin_pct >= 30 ? "green" : totals.margin_pct >= 10 ? "primary" : "warn";

  return (
    <div>
      <PageHeader
        overline="Analytics"
        title="Cost Summary"
        subtitle="Portfolio-level margin roll-up across every deal. Revenue counts money already collected — expenses are the sum of the deal's Cost Summary line items."
      />
      <div className="px-6 lg:px-10 pb-10 space-y-6" data-testid="cost-summary-page">
        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile label="Revenue collected" value={fmt(totals.revenue, currency)} icon={Coins} tone="green" testId="cs-kpi-revenue" />
          <KpiTile label="Expenses recorded" value={fmt(totals.expenses, currency)} icon={ArrowDownRight} tone="warn" testId="cs-kpi-expenses" />
          <KpiTile
            label="Portfolio profit"
            value={fmt(totals.profit, currency)}
            hint={pct(totals.margin_pct)}
            icon={totals.profit >= 0 ? TrendingUp : TrendingDown}
            tone={marginTone}
            testId="cs-kpi-profit"
          />
          <KpiTile
            label="Deals closed"
            value={`${totals.closed_count || 0} / ${totals.deal_count || 0}`}
            hint={readyCount > 0 ? `${readyCount} ready to finalize` : undefined}
            hintTone={readyCount > 0 ? "primary" : "muted"}
            icon={CheckCircle2}
            tone="primary"
            testId="cs-kpi-closed"
          />
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1 bg-secondary/40 rounded-sm p-1" role="tablist" data-testid="cs-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                data-testid={`cs-status-${t.key}`}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold uppercase tracking-wider transition-colors ${
                  status === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
                {t.key === "ready" && readyCount > 0 && (
                  <span className="ml-1.5 inline-block px-1 py-0.5 rounded-sm bg-amber-500/20 text-amber-500 text-[9px]">{readyCount}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deals…" className="pl-9 rounded-sm bg-secondary/40" data-testid="cs-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px] rounded-sm bg-secondary/40" data-testid="cs-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="cs-table">
          <div className="grid grid-cols-[2fr_1.1fr_1.1fr_1.1fr_1.1fr_1fr_120px] gap-3 px-4 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
            <div>Deal</div>
            <div className="text-right">Invoiced</div>
            <div className="text-right">Revenue</div>
            <div className="text-right">Expenses</div>
            <div className="text-right">Profit</div>
            <div className="text-right">Margin</div>
            <div>Status</div>
          </div>
          {isLoading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && filtered.length === 0 && (
            <div className="p-12 text-center">
              <Coins className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <div className="text-sm text-muted-foreground">
                {q ? "No deals match your search." : "No deals in this bucket yet."}
              </div>
            </div>
          )}
          {filtered.map((d) => (
            <DealRow key={d.deal_id} deal={d} isTop={bestDeal?.deal_id === d.deal_id && (d.profit || 0) > 0} />
          ))}
        </Card>

        {/* Footer note */}
        {deals.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            Revenue is <span className="text-foreground font-semibold">only</span> what customers have paid — pending invoices don't count. Fill product cost prices in <Link to="/products" className="underline text-primary">Products</Link> to sharpen deal-level margins.
          </div>
        )}
      </div>
    </div>
  );
}

function DealRow({ deal, isTop }) {
  const marginTone = deal.margin_pct == null
    ? "text-muted-foreground"
    : deal.margin_pct >= 30 ? "text-green-400"
    : deal.margin_pct >= 10 ? "text-primary"
    : deal.margin_pct >= 0 ? "text-amber-400"
    : "text-red-400";
  return (
    <Link
      to={`/deals/${deal.deal_id}`}
      data-testid={`cs-row-${deal.deal_id}`}
      className="grid grid-cols-[2fr_1.1fr_1.1fr_1.1fr_1.1fr_1fr_120px] gap-3 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-secondary/30 transition-colors"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold truncate flex items-center gap-1.5">
          {isTop && <Flame className="h-3.5 w-3.5 text-amber-400 shrink-0" title="Highest profit deal" />}
          <span className="truncate">{deal.name}</span>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {deal.stage || "no stage"} · {deal.tasks_total > 0 ? `${deal.tasks_done}/${deal.tasks_total} tasks` : "no tasks"}
          {deal.expense_count > 0 && ` · ${deal.expense_count} expense${deal.expense_count === 1 ? "" : "s"}`}
        </div>
      </div>
      <div className="text-xs text-right text-muted-foreground">{fmt(deal.invoiced, deal.currency)}</div>
      <div className="text-sm font-semibold text-right text-green-400">{fmt(deal.revenue, deal.currency)}</div>
      <div className="text-xs text-right text-muted-foreground">{fmt(deal.expenses, deal.currency)}</div>
      <div className={`text-sm font-semibold text-right ${(deal.profit || 0) >= 0 ? "text-foreground" : "text-red-400"}`}>{fmt(deal.profit, deal.currency)}</div>
      <div className="text-right">
        <div className={`text-sm font-bold ${marginTone}`} title="Profit ÷ Revenue">{pct(deal.margin_pct)}</div>
        {deal.margin_pct != null && (
          <div className="mt-1 h-1.5 w-full bg-secondary/60 rounded-sm overflow-hidden">
            <div
              className={`h-full ${deal.margin_pct >= 30 ? "bg-green-500" : deal.margin_pct >= 10 ? "bg-primary" : deal.margin_pct >= 0 ? "bg-amber-500" : "bg-red-500"}`}
              style={{ width: `${Math.min(100, Math.max(0, deal.margin_pct))}%` }}
            />
          </div>
        )}
      </div>
      <div>
        {deal.is_closed ? (
          <Badge className="rounded-sm text-[9px] bg-green-500/10 text-green-500 border border-green-500/40 uppercase tracking-widest"><ShieldCheck className="h-2.5 w-2.5 mr-1 inline" />Finalized</Badge>
        ) : deal.ready_to_close ? (
          <Badge className="rounded-sm text-[9px] bg-amber-500/10 text-amber-500 border border-amber-500/40 uppercase tracking-widest"><Timer className="h-2.5 w-2.5 mr-1 inline" />Ready</Badge>
        ) : (
          <Badge variant="outline" className="rounded-sm text-[9px] uppercase tracking-widest">Open</Badge>
        )}
      </div>
    </Link>
  );
}

function KpiTile({ label, value, hint, icon: Icon, tone = "neutral", hintTone = "muted", testId }) {
  const toneCls = {
    neutral: "text-foreground",
    primary: "text-primary",
    green: "text-green-400",
    warn: "text-amber-400",
  }[tone];
  const hintCls = { muted: "text-muted-foreground", primary: "text-primary" }[hintTone];
  return (
    <Card className="rounded-sm bg-card border-border p-4 relative overflow-hidden" data-testid={testId}>
      {Icon && <Icon className="absolute -right-2 -bottom-2 h-16 w-16 text-secondary/30 -z-0" strokeWidth={1} />}
      <div className="relative z-10">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
        <div className={`font-display text-2xl font-black mt-1 ${toneCls}`}>{value}</div>
        {hint && <div className={`text-[11px] mt-0.5 ${hintCls}`}>{hint}</div>}
      </div>
    </Card>
  );
}
